import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useSubscription } from '../hooks/useSubscription'
import { useAuth } from '../hooks/useSupabaseAuth'
import NoSubscriptionOverlay from './NoSubscriptionOverlay'
import { Loader } from 'lucide-react'

/**
 * Wrapper component yang mengecek subscription sebelum render content
 * Menampilkan loading state dan error overlay jika subscription tidak valid
 * Reusable untuk semua agent
 */
const AgentSubscriptionWrapper = ({ actionType, children }) => {
  const { user } = useAuth()
  const { canPerformAction, loading } = useSubscription()
  const [actionResult, setActionResult] = useState(null)
  const [checking, setChecking] = useState(true)

  useEffect(() => {
    const checkAccess = async () => {
      if (!user || loading) {
        setChecking(true)
        return
      }

      try {
        const result = await canPerformAction(actionType)
        setActionResult(result)
      } catch (error) {
        console.error('Error checking action access:', error)
        setActionResult({
          canPerform: false,
          reason: 'error',
          message: 'Terjadi kesalahan saat memeriksa akses agen'
        })
      } finally {
        setChecking(false)
      }
    }

    checkAccess()
  }, [user, loading, actionType, canPerformAction])

  // Show loading state
  if (loading || checking || !actionResult) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-8 h-8 animate-spin mx-auto mb-4 text-brand-primary" />
          <p className="text-text-tertiary">Memeriksa akses...</p>
        </div>
      </div>
    )
  }

  // Show error overlay if can't perform action
  if (!actionResult.canPerform) {
    return (
      <NoSubscriptionOverlay 
        reason={actionResult.reason}
        message={actionResult.message}
        needsUpgrade={actionResult.needsUpgrade}
        packageName={actionResult.packageName}
        creditsRemaining={actionResult.creditsRemaining}
        creditsRequired={actionResult.creditsRequired}
        isFreeUser={actionResult.isFreeUser}
        onRetry={() => {
          setChecking(true)
          setActionResult(null)
        }}
      />
    )
  }

  // Render children if access is granted
  return children
}

AgentSubscriptionWrapper.propTypes = {
  actionType: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired
}

export default AgentSubscriptionWrapper